/**
 * Keyboard shortcut utilities for the interactive diagram.
 */

/**
 * Returns true if the active element is a text input (input, textarea,
 * or contenteditable), meaning keyboard shortcuts should be suppressed.
 */
export function isEditingText(): boolean {
    const el = document.activeElement
    if (!el) return false

    const tag = el.tagName.toLowerCase()
    if (tag === 'input' || tag === 'textarea') return true
    if ((el as HTMLElement).isContentEditable) return true

    return false
}

/**
 * Register a keyboard shortcut on `window`. Returns a cleanup function
 * that removes the listener.
 *
 * `key` is compared case-insensitively against `KeyboardEvent.key`.
 */
export function onKeyCombo(
    key: string,
    modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean },
    handler: () => void,
): () => void {
    const listener = (e: KeyboardEvent) => {
        if (isEditingText()) return

        const ctrlMatch = (modifiers.ctrl ?? false) === (e.ctrlKey || e.metaKey)
        const shiftMatch = (modifiers.shift ?? false) === e.shiftKey
        const altMatch = (modifiers.alt ?? false) === e.altKey

        if (ctrlMatch && shiftMatch && altMatch && e.key.toLowerCase() === key.toLowerCase()) {
            e.preventDefault()
            handler()
        }
    }

    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
}
